/**
 * Citation token registry for the PETCI AI assistant (ADR-014).
 *
 * The AI model is constrained to emit only [bracket] tokens from this fixed
 * vocabulary. The server maps each token + active persona to a predefined
 * IntentCitation sourced from real account data — the model never authors
 * citation detail strings. Unknown brackets → validation fail → canned fallback.
 *
 * Token catalog: 13 tokens covering all persona types (residential, commercial,
 * hotel, PM). Tokens that don't apply to a persona (e.g. [demand live] for P1
 * residential) return null; all-null result → validation fail → canned fallback.
 */
import type { IntentCitation, Persona } from "@petci/fixtures";

export type CitationToken =
  | "fuel factor"
  | "bill trend"
  | "solar net-export"
  | "outage history"
  | "payment history"
  | "demand live"
  | "ecor live"
  | "industry benchmark"
  | "anomaly alert"
  | "portfolio bills"
  | "co2 data"
  | "storm ETA"
  | "data verified";

export const CITATION_TOKENS: readonly CitationToken[] = [
  "fuel factor",
  "bill trend",
  "solar net-export",
  "outage history",
  "payment history",
  "demand live",
  "ecor live",
  "industry benchmark",
  "anomaly alert",
  "portfolio bills",
  "co2 data",
  "storm ETA",
  "data verified",
];

export const CITATION_TOKEN_SET: ReadonlySet<string> = new Set(CITATION_TOKENS);

// ---------------------------------------------------------------------------
// Helper utilities
// ---------------------------------------------------------------------------

/** Maps persona.serviceAddress.island enum → display label. */
function islandLabel(id: string): string {
  const map: Record<string, string> = {
    PROVIDENCIALES: "Provo",
    GRAND_TURK: "Grand Turk",
    NORTH_CAICOS: "North Caicos",
    MIDDLE_CAICOS: "Middle Caicos",
    SOUTH_CAICOS: "South Caicos",
  };
  return map[id] ?? id;
}

/** "2026-05" → "May 2026" */
function formatBillingMonth(yyyyMM: string): string {
  const [y, m] = yyyyMM.split("-");
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthName = months[Number(m) - 1] ?? String(m);
  return `${monthName} ${String(y)}`;
}

/** ISO datetime → "22 May 2026 14:31" British-style. */
function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${String(d.getUTCDate())} ${String(months[d.getUTCMonth()])} ${String(d.getUTCFullYear())} ${hh}:${mm}`;
}

// ---------------------------------------------------------------------------
// Per-persona hard-coded payment history constants.
// ---------------------------------------------------------------------------
const PAYMENT_HISTORY: Record<string, string> = {
  P1: "Most recent: $283.40 paid last month (confirmation #PC-8821). No missed payments in the last 12 months.",
  P2: "Most recent: $1,142.00 paid last month (confirmation #PC-2203). No missed payments in the last 12 months.",
  P3: "Most recent: $9,440.00 paid last month (confirmation #PC-3309). No missed payments in the last 12 months.",
  P4: "Portfolio aggregate: 4 accounts current. Most recent: $312.00 paid last month (confirmation #PC-5530).",
  P5: "No payment history available for this account.",
};

export const STORM_ETA_DETAIL =
  "Tropical Storm Jolene (simulated) — projected landfall TCI within ~48 hours, 09:00 AST ±12h. " +
  "Source: synthetic preview data (Atlantic hurricane season: 1 Jun–30 Nov). " +
  "Real NOAA NHC data integration is planned for production.";

// ---------------------------------------------------------------------------
// Registry builder
// ---------------------------------------------------------------------------

/**
 * Build an IntentCitation for a given token + persona. Returns null if
 * the token doesn't apply to this persona type.
 */
export function buildCitation(
  token: CitationToken,
  persona: Persona,
): IntentCitation | null {
  const island = islandLabel(persona.serviceAddress.island);
  const monthLabel = formatBillingMonth(persona.currentBill.month);

  switch (token) {
    case "fuel factor": {
      const ff = persona.currentBill.fuelFactor;
      const effectiveRate = 0.2910 + ff;
      return {
        badgeLabel: "fuel factor",
        detail: `${island} fuel factor ${monthLabel}: $${ff.toFixed(4)}/kWh. Electric rate: $0.2910/kWh (constant). Effective rate: $${effectiveRate.toFixed(4)}/kWh. Source: account ${persona.accountNumber} current bill.`,
      };
    }

    case "bill trend": {
      const totals = persona.bills.map((b) => b.total);
      const min = Math.min(...totals);
      const max = Math.max(...totals);
      return {
        badgeLabel: "bill trend",
        detail: `${String(persona.bills.length)}-month range: $${min.toFixed(2)} (low) to $${max.toFixed(2)} (high). Current (${monthLabel}): $${persona.currentBill.total.toFixed(2)}. Source: account ${persona.accountNumber} bill history.`,
      };
    }

    case "solar net-export": {
      if (!persona.solar) return null;
      return {
        badgeLabel: "solar net-export",
        detail: `${String(persona.solar.capacityKW)} kW ${persona.solar.type} system, commissioned ${persona.solar.commissionedDate}. Demo constant: cumulative net-export to the Pelican Energy TCI grid ~8,400 kWh since commission. Source: account ${persona.accountNumber} CORE/UORE programme record.`,
      };
    }

    case "outage history": {
      if (!persona.outages.length) {
        return {
          badgeLabel: "outage history",
          detail: `No outages recorded for account ${persona.accountNumber} in the last 12 months. Source: OMS outage records.`,
        };
      }
      const recent = persona.outages
        .slice(0, 2)
        .map(
          (o) =>
            `${formatDateTime(o.startedAt)} (${String(o.durationMinutes)} min, ${o.cause})`,
        )
        .join("; prior: ");
      return {
        badgeLabel: "outage history",
        detail: `Account ${persona.accountNumber} outage log: ${recent}. Source: OMS outage records.`,
      };
    }

    case "payment history": {
      const detail = PAYMENT_HISTORY[persona.id] ?? "No payment history available.";
      return {
        badgeLabel: "payment history",
        detail: `${detail} Source: account ${persona.accountNumber} transaction ledger.`,
      };
    }

    case "demand live": {
      if (!persona.hotelKpi) return null;
      const kpi = persona.hotelKpi;
      const pctOfThreshold = ((kpi.peakDemandKW / kpi.peakDemandThresholdKW) * 100).toFixed(0);
      return {
        badgeLabel: "demand live",
        detail: `Peak demand ${monthLabel}: ${String(kpi.peakDemandKW)} kW of ${String(kpi.peakDemandThresholdKW)} kW contracted threshold (${pctOfThreshold}% utilisation). Source: account ${persona.accountNumber} metering data.`,
      };
    }

    case "ecor live": {
      if (!persona.hotelKpi) return null;
      const kpi = persona.hotelKpi;
      return {
        badgeLabel: "ecor live",
        detail: `ECOR (Energy Cost per Occupied Room-night) ${monthLabel}: $${kpi.ecorUSD.toFixed(2)} at ${String(kpi.occupancyPct)}% occupancy (${String(kpi.suites)} suites). Source: account ${persona.accountNumber} hotel KPI dashboard.`,
      };
    }

    case "industry benchmark": {
      if (!persona.hotelKpi) return null;
      const bench = persona.hotelKpi.industryBenchmarks;
      return {
        badgeLabel: "industry benchmark",
        detail: `TCI hotel industry benchmark ECOR: $${bench.ecorUSD.toFixed(2)}/room-night; ${String(bench.pctRenewable)}% renewable; ${String(bench.kWhPerGuestNight)} kWh/guest-night. Source: APPA Caribbean Hospitality Energy Index 2024.`,
      };
    }

    case "anomaly alert": {
      const flagged = persona.managedAccounts?.filter((a) => a.anomalyBadge) ?? [];
      if (!flagged.length) return null;
      const list = flagged.map((a) => `${a.propertyName} (${a.anomalyBadge ?? ""})`).join(", ");
      return {
        badgeLabel: "anomaly alert",
        detail: `Anomaly detected on ${String(flagged.length)} of ${String(persona.managedAccounts?.length ?? 0)} managed accounts: ${list}. Source: account ${persona.accountNumber} portfolio metering analysis.`,
      };
    }

    case "portfolio bills": {
      if (!persona.managedAccounts?.length) return null;
      const total = persona.managedAccounts.reduce((s, a) => s + a.currentBillAmount, 0);
      const list = persona.managedAccounts
        .map((a) => `${a.propertyName} $${a.currentBillAmount.toFixed(2)}`)
        .join(", ");
      return {
        badgeLabel: "portfolio bills",
        detail: `Portfolio aggregate (${String(persona.managedAccounts.length)} accounts, ${monthLabel}): $${total.toFixed(2)} total. Breakdown: ${list}. Source: account ${persona.accountNumber} portfolio ledger.`,
      };
    }

    case "co2 data": {
      if (!persona.hotelKpi) return null;
      const kpi = persona.hotelKpi;
      return {
        badgeLabel: "co2 data",
        detail: `Scope-2 emissions ${monthLabel}: ${String(kpi.co2eTonnesMonthly)} t CO₂e (grid factor 0.70 kg CO₂/kWh; ${String(kpi.pctRenewable)}% renewable offset applied). Source: account ${persona.accountNumber} ESG dashboard.`,
      };
    }

    case "storm ETA": {
      return {
        badgeLabel: "storm ETA",
        detail: STORM_ETA_DETAIL,
      };
    }

    case "data verified": {
      return {
        badgeLabel: "data verified",
        detail: "Response is being verified against account records. Standard explanation shown while live data is retrieved.",
      };
    }
  }
}

/**
 * Strips bracket tokens from text and returns both the cleaned text and
 * resolved citations for all recognized tokens.
 * Used by the mobile fallback path to avoid leaking raw `[usage Δ]` tokens.
 */
export function resolveTokensInText(
  text: string,
  persona: Persona,
): { cleanText: string; citations: IntentCitation[] } {
  const citations: IntentCitation[] = [];
  const cleanText = text.replace(/\[([^\]]+)\]/g, (_match, tokenStr: string) => {
    if (CITATION_TOKEN_SET.has(tokenStr)) {
      const cit = buildCitation(tokenStr as CitationToken, persona);
      if (cit) citations.push(cit);
    }
    return "";
  });
  return { cleanText: cleanText.replace(/ {2,}/g, " ").trim(), citations };
}
